// Rumus kesehatan keuangan. MURNI — tak menyentuh basis data, supaya bisa diuji apa adanya.
//
// Basis tetap KAS (keputusan Apo, lihat CLAUDE.md). Yang ditambahkan di sini hanyalah
// penyajian & indikator; tak ada aturan uang lama yang diubah.

/**
 * Metode penyusutan yang DIAKUI di Indonesia — UU PPh Pasal 11 hanya mengenal dua:
 * garis lurus dan saldo menurun. Bangunan WAJIB garis lurus.
 */
export type MetodeSusut = 'garis_lurus' | 'saldo_menurun';

/**
 * Kelompok harta berwujud (UU PPh Ps. 11 / PMK 96/2009) beserta masa manfaatnya dalam TAHUN.
 * Dipakai mengisi umur otomatis saat Apo memilih kelompok; nilainya tetap boleh diubah manual
 * bila akuntan menghendaki umur ekonomis yang berbeda dari umur fiskal.
 */
export const KELOMPOK_ASET = {
  '1': { label: 'Kelompok 1 — 4 tahun (komputer, HP, perkakas)', tahun: 4 },
  '2': { label: 'Kelompok 2 — 8 tahun (mebel, AC, kendaraan)', tahun: 8 },
  '3': { label: 'Kelompok 3 — 16 tahun (mesin berat)', tahun: 16 },
  '4': { label: 'Kelompok 4 — 20 tahun', tahun: 20 },
  bangunan_permanen: { label: 'Bangunan permanen — 20 tahun', tahun: 20 },
  bangunan_non: { label: 'Bangunan tidak permanen — 10 tahun', tahun: 10 },
} as const;
export type KelompokAset = keyof typeof KELOMPOK_ASET;

/** Bangunan hanya boleh garis lurus (Ps. 11 ayat 6) — dikunci di aplikasi, bukan sekadar imbauan. */
export const wajibGarisLurus = (k?: string | null): boolean => (k ?? '').startsWith('bangunan');

export interface Aset {
  nama: string;
  /** Harga perolehan (Rp). */
  harga: number;
  tglBeli: string;          // yyyy-mm-dd
  /** Umur ekonomis (bulan). 0 = tidak disusutkan (mis. tanah). */
  umurBulan: number;
  /** Nilai sisa saat umur habis (Rp). */
  nilaiSisa?: number;
  dijualTgl?: string | null;
  /** Kosong = garis lurus (metode paling lazim di UMKM). */
  metodeSusut?: MetodeSusut | null;
  kelompok?: string | null;
}

/** Jumlah bulan penuh dari `a` ke `b` (a lebih awal). */
export function selisihBulan(a: string, b: string): number {
  const [ya, ma, da] = a.split('-').map(Number) as [number, number, number];
  const [yb, mb, db] = b.split('-').map(Number) as [number, number, number];
  let n = (yb - ya) * 12 + (mb - ma);
  if (db < da) n -= 1;                       // belum genap sebulan
  return Math.max(0, n);
}

/**
 * Penyusutan garis lurus per BULAN. Berhenti setelah umur habis — aset yang sudah habis
 * disusutkan tak boleh terus mengurangi laba (kesalahan klasik yang membuat laba terlihat
 * lebih kecil dari kenyataan selama bertahun-tahun).
 */
export function penyusutanBulanan(a: Aset): number {
  if (a.umurBulan <= 0 || a.harga <= 0) return 0;
  return Math.max(0, a.harga - (a.nilaiSisa ?? 0)) / a.umurBulan;
}

const pakaiSaldoMenurun = (a: Aset): boolean =>
  a.metodeSusut === 'saldo_menurun' && !wajibGarisLurus(a.kelompok) && a.umurBulan > 0 && a.harga > 0;

/**
 * Penyusutan SALDO MENURUN (declining balance) pada bulan ke-`bulanKe` (0 = bulan pertama).
 *
 * Tarifnya dua kali garis lurus atas NILAI BUKU — jadi beban besar di awal, mengecil kemudian.
 * Aturan penutup UU PPh Ps. 11 ayat (2): pada AKHIR masa manfaat, sisa nilai bukunya disusutkan
 * sekaligus; tanpa itu nilai buku tak pernah mencapai nol (sifat matematis metode ini) dan aset
 * yang sudah tak berguna terus tercatat sebagai kekayaan.
 */
function susutSaldoMenurunBulan(a: Aset, bulanKe: number): number {
  const sisaAkhir = a.nilaiSisa ?? 0;
  const tarifBulanan = 2 / a.umurBulan;                 // 2× garis lurus
  let buku = a.harga;
  for (let i = 0; i < bulanKe; i++) {
    if (i === a.umurBulan - 1) { buku = sisaAkhir; break; }
    buku = Math.max(sisaAkhir, buku - Math.max(0, buku - sisaAkhir) * tarifBulanan);
  }
  if (bulanKe >= a.umurBulan) return 0;
  if (bulanKe === a.umurBulan - 1) return Math.max(0, buku - sisaAkhir);   // bulan terakhir: habiskan
  return Math.max(0, buku - sisaAkhir) * tarifBulanan;
}

/** Penyusutan yang jatuh pada satu bulan `ym` (yyyy-mm). 0 bila belum dibeli / sudah habis / sudah dijual. */
export function penyusutanPadaBulan(a: Aset, ym: string): number {
  const akhirBulan = `${ym}-28`;                       // aman untuk semua bulan
  if (a.tglBeli.slice(0, 7) > ym) return 0;
  if (a.dijualTgl && a.dijualTgl.slice(0, 7) <= ym) return 0;
  const jalan = selisihBulan(a.tglBeli, akhirBulan);
  if (jalan >= a.umurBulan) return 0;
  return pakaiSaldoMenurun(a) ? susutSaldoMenurunBulan(a, jalan) : penyusutanBulanan(a);
}

/** Nilai buku aset pada tanggal tertentu = harga − akumulasi penyusutan (tak pernah di bawah nilai sisa). */
export function nilaiBuku(a: Aset, sampai: string): number {
  if (a.dijualTgl && a.dijualTgl <= sampai) return 0;
  const bulanJalan = Math.min(selisihBulan(a.tglBeli, sampai), a.umurBulan || 0);
  if (pakaiSaldoMenurun(a)) {
    let akum = 0;
    for (let i = 0; i < bulanJalan; i++) akum += susutSaldoMenurunBulan(a, i);
    return Math.max(a.nilaiSisa ?? 0, a.harga - akum);
  }
  const akum = penyusutanBulanan(a) * bulanJalan;
  return Math.max(a.nilaiSisa ?? 0, a.harga - akum);
}

// ── Indikator kesehatan ───────────────────────────────────────────────────────
export const persen = (bagian: number, dari: number): number => (dari > 0 ? (bagian / dari) * 100 : 0);

/** Ketahanan kas (bulan) = kas ÷ rata-rata beban bulanan. Infinity bila tak ada beban. */
export function ketahananKas(kas: number, bebanPerBulan: number): number {
  if (bebanPerBulan <= 0) return Infinity;
  return Math.max(0, kas) / bebanPerBulan;
}

/** DSO — rata-rata hari sebuah tagihan tertagih. 0 bila belum ada pendapatan. */
export function dso(piutang: number, pendapatan: number, hariPeriode: number): number {
  if (pendapatan <= 0 || hariPeriode <= 0) return 0;
  return (piutang / pendapatan) * hariPeriode;
}

/**
 * Titik impas (Rp omzet) = beban tetap ÷ margin kotor.
 * Margin kotor 0 atau minus → tak ada titik impas: berapa pun jualannya tetap rugi.
 */
export function bep(bebanTetap: number, marginKotorPersen: number): number | null {
  if (marginKotorPersen <= 0) return null;
  return bebanTetap / (marginKotorPersen / 100);
}

/** Perputaran persediaan (kali per tahun). */
export function perputaranPersediaan(hpp: number, rataPersediaan: number): number {
  if (rataPersediaan <= 0) return 0;
  return hpp / rataPersediaan;
}

/** Konsentrasi pelanggan: % pendapatan dari `n` pelanggan terbesar. */
export function konsentrasi(nilaiPerPelanggan: number[], n = 5): { persen: number; teratas: number } {
  const total = nilaiPerPelanggan.reduce((a, b) => a + b, 0);
  if (total <= 0) return { persen: 0, teratas: 0 };
  const urut = [...nilaiPerPelanggan].sort((a, b) => b - a);
  const atas = urut.slice(0, n).reduce((a, b) => a + b, 0);
  return { persen: (atas / total) * 100, teratas: persen(urut[0] ?? 0, total) };
}

// ── Analisa berbasis aturan ───────────────────────────────────────────────────
export type Tingkat = 'baik' | 'perhatian' | 'bahaya';
// drill = kunci sumber masalah (dipakai UI untuk "klik → ke layar sumbernya"): labarugi/aruskas/piutang/crm/lapservis.
export interface Temuan { tingkat: Tingkat; judul: string; pesan: string; drill?: string }

export interface AmbangSehat {
  kasBulanBahaya: number; kasBulanPerhatian: number;
  macetPersen: number; konsentrasiPersen: number; kualitasPersen: number;
  turunBerturut: number;
}
export const DEFAULT_AMBANG: AmbangSehat = {
  kasBulanBahaya: 1, kasBulanPerhatian: 2,
  macetPersen: 10, konsentrasiPersen: 30, kualitasPersen: 5, turunBerturut: 3,
};

export interface BahanAnalisa {
  pendapatan: number; labaBersih: number; marginBersihPersen: number;
  marginBulanTerakhir: number[];       // urut lama → baru
  kasTahan: number;                    // bulan
  piutangMacet: number;
  konsentrasiTeratas: number;          // % pelanggan terbesar
  biayaKualitasPersen: number;
  bepRp: number | null;
}

/**
 * Membaca angka lalu menuliskan temuannya dalam kalimat manusia.
 * Sengaja BERBASIS ATURAN, bukan AI: hasilnya bisa ditelusuri ("kenapa muncul peringatan ini")
 * dan angka keuangan toko tak pernah dikirim keluar.
 */
export function analisaSehat(b: BahanAnalisa, amb: AmbangSehat = DEFAULT_AMBANG): Temuan[] {
  const out: Temuan[] = [];

  if (b.pendapatan <= 0) {
    out.push({ tingkat: 'perhatian', judul: 'Belum ada pendapatan', pesan: 'Tak ada transaksi pada periode ini, jadi indikator lain belum bisa dibaca.' });
    return out;
  }

  if (b.labaBersih < 0) {
    out.push({ tingkat: 'bahaya', judul: 'Rugi pada periode ini', pesan: `Laba bersih minus. Periksa beban terbesar dan harga jual jasa.`, drill: 'labarugi' });
  } else if (b.marginBersihPersen < 5) {
    out.push({ tingkat: 'perhatian', judul: 'Margin tipis', pesan: `Margin bersih ${b.marginBersihPersen.toFixed(1)}% — sedikit kenaikan beban sudah cukup membuat rugi.`, drill: 'labarugi' });
  }

  // Margin turun berturut-turut = arah, bukan sekadar angka satu periode.
  const m = b.marginBulanTerakhir;
  let turun = 0;
  for (let i = m.length - 1; i > 0; i--) { if ((m[i] as number) < (m[i - 1] as number)) turun++; else break; }
  if (turun >= amb.turunBerturut) {
    out.push({ tingkat: 'perhatian', judul: 'Margin turun berturut-turut', pesan: `Margin menurun ${turun} periode beruntun — periksa harga jasa, harga sparepart, atau banyaknya diskon.`, drill: 'labarugi' });
  }

  if (b.kasTahan < amb.kasBulanBahaya) {
    out.push({ tingkat: 'bahaya', judul: 'Kas menipis', pesan: `Kas hanya cukup untuk ${b.kasTahan.toFixed(1)} bulan beban. Kejar piutang & tunda belanja yang bisa ditunda.`, drill: 'aruskas' });
  } else if (b.kasTahan < amb.kasBulanPerhatian) {
    out.push({ tingkat: 'perhatian', judul: 'Kas perlu diperhatikan', pesan: `Kas cukup untuk ${b.kasTahan.toFixed(1)} bulan beban.`, drill: 'aruskas' });
  }

  const macetP = persen(b.piutangMacet, b.pendapatan);
  if (macetP > amb.macetPersen) {
    out.push({ tingkat: 'perhatian', judul: 'Piutang macet menumpuk', pesan: `Piutang di atas 90 hari setara ${macetP.toFixed(0)}% pendapatan — uang sudah bekerja tapi belum kembali.`, drill: 'piutang' });
  }

  if (b.konsentrasiTeratas > amb.konsentrasiPersen) {
    out.push({ tingkat: 'perhatian', judul: 'Terlalu bergantung pada satu pelanggan', pesan: `${b.konsentrasiTeratas.toFixed(0)}% pendapatan dari satu pelanggan — kalau ia berhenti, dampaknya besar.`, drill: 'crm' });
  }

  if (b.biayaKualitasPersen > amb.kualitasPersen) {
    out.push({ tingkat: 'perhatian', judul: 'Biaya servis ulang tinggi', pesan: `Servis ulang & garansi setara ${b.biayaKualitasPersen.toFixed(1)}% pendapatan — keuntungan tergerus pekerjaan yang harus diulang.`, drill: 'lapservis' });
  }

  if (b.bepRp !== null && b.pendapatan < b.bepRp) {
    out.push({ tingkat: 'bahaya', judul: 'Belum mencapai titik impas', pesan: `Pendapatan masih di bawah titik impas periode ini.`, drill: 'labarugi' });
  }

  if (!out.length) out.push({ tingkat: 'baik', judul: 'Keuangan sehat', pesan: 'Margin, kas, piutang, dan ketergantungan pelanggan semuanya dalam batas wajar.' });
  return out;
}
